(() => {
  'use strict';

  const SECTION_SEL = '.hiw-sp';
  const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);

  const MOBILE_QUERY = '(max-width: 860px)';

  class HiwSuperpower {
    constructor(section) {
      this.section = section;
      this.stack = section.querySelector('[data-hiw-sp-stack]');
      this.cards = Array.from(section.querySelectorAll('[data-hiw-sp-card]'));
      this.dotsWrap = section.querySelector('[data-hiw-sp-dots]');
      this.dots = Array.from(section.querySelectorAll('[data-dot]'));
      this.ticking = false;
      this.activeIndex = -1;
      this.prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.mobileMQ = window.matchMedia(MOBILE_QUERY);
      this.isMobile = this.mobileMQ.matches;
      this.mobileObserver = null;

      this._onScroll = this._onScroll.bind(this);
      this._onResize = this._onResize.bind(this);
      this._tick = this._tick.bind(this);
      this._onMQChange = this._onMQChange.bind(this);

      this.dots.forEach(dot => {
        dot.addEventListener('click', () => {
          const idx = parseInt(dot.dataset.dot, 10);
          if (this.cards[idx]) {
            this.cards[idx].scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        });
      });

      this.init();
    }

    init() {
      if (this.prefersReduced) {
        this.cards.forEach(c => {
          c.classList.add('is-active');
          c.classList.add('is-in-view');
        });
        if (this.dotsWrap) this.dotsWrap.classList.add('is-visible');
        return;
      }

      if (this.mobileMQ.addEventListener) {
        this.mobileMQ.addEventListener('change', this._onMQChange);
      } else if (this.mobileMQ.addListener) {
        this.mobileMQ.addListener(this._onMQChange);
      }

      if (this.isMobile) {
        this._enableMobile();
      } else {
        this._enableDesktop();
      }
    }

    _enableDesktop() {
      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onResize, { passive: true });
      requestAnimationFrame(this._tick);
    }

    _disableDesktop() {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onResize);
    }

    _enableMobile() {
      // Reset any inline transforms from desktop mode.
      this.cards.forEach(card => {
        card.style.removeProperty('--card-scale');
        card.style.removeProperty('--card-opacity');
      });
      if (this.dotsWrap) this.dotsWrap.classList.remove('is-visible');

      if (!('IntersectionObserver' in window)) {
        this.cards.forEach(c => c.classList.add('is-in-view'));
        return;
      }

      this.mobileObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in-view');
            this.mobileObserver.unobserve(entry.target);
          }
        });
      }, { threshold: 0.2, rootMargin: '0px 0px -10% 0px' });

      this.cards.forEach(card => this.mobileObserver.observe(card));
    }

    _disableMobile() {
      if (this.mobileObserver) {
        this.mobileObserver.disconnect();
        this.mobileObserver = null;
      }
      this.cards.forEach(c => c.classList.remove('is-in-view'));
    }

    _onMQChange(e) {
      const nowMobile = e.matches;
      if (nowMobile === this.isMobile) return;
      this.isMobile = nowMobile;

      if (nowMobile) {
        this._disableDesktop();
        this._enableMobile();
      } else {
        this._disableMobile();
        this._enableDesktop();
      }
    }

    _onScroll() {
      if (!this.ticking) {
        this.ticking = true;
        requestAnimationFrame(this._tick);
      }
    }

    _onResize() {
      requestAnimationFrame(this._tick);
    }

    _tick() {
      this.ticking = false;

      const sectionRect = this.section.getBoundingClientRect();
      const vh = window.innerHeight;

      if (sectionRect.bottom < -200 || sectionRect.top > vh + 200) {
        if (this.dotsWrap) this.dotsWrap.classList.remove('is-visible');
        return;
      }

      if (this.dotsWrap) {
        const dotsVisible = sectionRect.top < vh * 0.5 && sectionRect.bottom > vh * 0.3;
        this.dotsWrap.classList.toggle('is-visible', dotsVisible);
      }

      let newActive = 0;

      for (let i = 0; i < this.cards.length; i++) {
        const card = this.cards[i];
        const rect = card.getBoundingClientRect();

        if (rect.top <= vh * 0.25) {
          newActive = i;
          card.classList.add('is-active');
        }

        const nextCard = this.cards[i + 1];
        if (!nextCard) continue;

        const nextRect = nextCard.getBoundingClientRect();
        const nextTop = nextRect.top;

        // Fade starts when the next card's top enters the viewport (nextTop < vh)
        // Fully faded when the next card reaches ~20% from top (nextTop < vh * 0.2)
        const fadeStart = vh;
        const fadeEnd = vh * 0.15;

        if (nextTop < fadeStart) {
          const raw = (fadeStart - nextTop) / (fadeStart - fadeEnd);
          const progress = clamp(raw, 0, 1);
          const eased = progress < 0.5
            ? 2 * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 2) / 2;

          const scale = 1 - eased * 0.1;
          const opacity = 1 - eased;

          card.style.setProperty('--card-scale', scale.toFixed(4));
          card.style.setProperty('--card-opacity', opacity.toFixed(4));
        } else {
          card.style.setProperty('--card-scale', '1');
          card.style.setProperty('--card-opacity', '1');
        }
      }

      if (newActive !== this.activeIndex) {
        this.activeIndex = newActive;
        this.dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i <= newActive);
        });
      }
    }

    destroy() {
      this._disableDesktop();
      this._disableMobile();
      if (this.mobileMQ.removeEventListener) {
        this.mobileMQ.removeEventListener('change', this._onMQChange);
      } else if (this.mobileMQ.removeListener) {
        this.mobileMQ.removeListener(this._onMQChange);
      }
    }
  }

  const initAll = () => {
    document.querySelectorAll(SECTION_SEL).forEach(section => {
      if (section.__hiwSP) return;
      section.__hiwSP = new HiwSuperpower(section);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (e) => {
    const ct = e.target;
    const sec = ct.matches(SECTION_SEL) ? ct : ct.querySelector(SECTION_SEL);
    if (!sec || sec.__hiwSP) return;
    sec.__hiwSP = new HiwSuperpower(sec);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const ct = e.target;
    const sec = ct.matches(SECTION_SEL) ? ct : ct.querySelector(SECTION_SEL);
    if (!sec || !sec.__hiwSP) return;
    sec.__hiwSP.destroy();
    delete sec.__hiwSP;
  });
})();
